import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Action, Field, Panel, PanelHeader, StatusBadge } from "./ui";

export const formatDay = (value) =>
  value ? String(value).slice(0, 10).split("-").reverse().join("/") : "—";

export const LEVEL_TONE = { normal: "active", attention: "pending", alert: "alert", unknown: "neutral" };

const newToken = () =>
  (window.crypto?.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;

const todayIso = () => new Date().toISOString().slice(0, 10);
const nowTime = () => new Date().toTimeString().slice(0, 5);

export const useConversationOptions = (ward) => {
  const { api } = useAuth();
  const [options, setOptions] = useState(null);
  useEffect(() => {
    api
      .get("/portal/conversations/options", { params: { ward: ward || undefined } })
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward]);
  return options;
};

export const useEligiblePatients = (counselorId, ward, enabled = true) => {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api
      .get("/portal/conversations/eligible-patients", { params: { counselor_id: counselorId || undefined, ward } })
      .then(({ data }) => setItems(data.items))
      .catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counselorId, ward, enabled]);
  return items;
};

const Modal = ({ title, hint, children, onClose, testId }) => (
  <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/40 p-0 sm:items-center sm:p-6">
    <div
      data-testid={testId}
      className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl"
    >
      <div className="mb-4">
        <h2 className="font-display text-[20px] font-semibold text-ink-900">{title}</h2>
        {hint ? <p className="mt-1 text-[13.5px] text-ink-500">{hint}</p> : null}
      </div>
      {children}
      <button
        type="button"
        onClick={onClose}
        data-testid={`${testId}-close`}
        className="mt-4 w-full rounded-xl border border-border py-2.5 text-[14.5px] font-semibold text-ink-700 hover:bg-sand"
      >
        Fechar
      </button>
    </div>
  </div>
);

const CounselorField = ({ options, value, onChange, testId, id }) => {
  const locked = options ? !options.can_select_counselor : false;
  const own = options?.counselors?.find((c) => c.id === options?.own_counselor_id);
  return (
    <Field label="Conselheiro" htmlFor={id} hint={locked ? "Conversa registrada em seu nome." : undefined}>
      {locked ? (
        <div
          id={id}
          data-testid={`${testId}-locked`}
          className="flex h-11 items-center rounded-xl border border-border bg-sand px-3.5 text-[14.5px] font-semibold text-ink-700"
        >
          {own?.name || "Você"}
        </div>
      ) : (
        <select
          id={id}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
        >
          <option value="">Selecionar</option>
          {(options?.counselors || []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
};

const PatientField = ({ patients, value, onChange, testId, id }) => (
  <Field label="Paciente" htmlFor={id} hint={patients.length === 0 ? "Nenhum paciente disponível." : undefined}>
    <select
      id={id}
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
      required
    >
      <option value="">Selecionar</option>
      {patients.map((p) => (
        <option key={p.id} value={p.id}>
          {p.full_name}
        </option>
      ))}
    </select>
  </Field>
);

// Registrar conversa realizada: primeiro define se havia agendamento, depois conclui o registro.
export const ConversationModal = ({ patient: initialPatient, schedule, onClose, onSaved }) => {
  const { api } = useAuth();
  const options = useConversationOptions();
  const [token] = useState(newToken);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(schedule ? "form" : "choose");
  const [patient, setPatient] = useState(initialPatient || null);
  const [pending, setPending] = useState(null);
  const [picked, setPicked] = useState(schedule || null);
  const [counselorId, setCounselorId] = useState(
    schedule?.counselor_id || initialPatient?.counselor_id || ""
  );
  const patients = useEligiblePatients(counselorId, undefined, !initialPatient);
  const [form, setForm] = useState({
    conversation_date: schedule?.scheduled_date || todayIso(),
    conversation_time: schedule?.scheduled_time || nowTime(),
    contact_type: "presencial",
    duration_minutes: schedule?.duration_minutes || "",
    notes: "",
  });

  useEffect(() => {
    if (options && !options.can_select_counselor && options.own_counselor_id) {
      setCounselorId(options.own_counselor_id);
    }
  }, [options]);

  const loadPending = async () => {
    if (!patient?.id) {
      toast.error("Selecione o paciente.");
      return;
    }
    setPending(null);
    setStep("pending");
    try {
      const { data } = await api.get(`/portal/conversations/patients/${patient.id}`);
      setPending(data.schedules || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setPending([]);
    }
  };

  const choosePending = (item) => {
    setPicked(item);
    setCounselorId(item.counselor_id || counselorId);
    setForm({
      ...form,
      conversation_date: item.scheduled_date,
      conversation_time: item.scheduled_time,
      duration_minutes: item.duration_minutes || "",
    });
    setStep("form");
  };

  const startUnscheduled = () => {
    if (!patient?.id) {
      toast.error("Selecione o paciente.");
      return;
    }
    setPicked(null);
    setStep("form");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/portal/conversations", {
        patient_id: patient.id,
        counselor_id: counselorId || undefined,
        conversation_date: form.conversation_date,
        conversation_time: form.conversation_time,
        contact_type: form.contact_type,
        duration_minutes: form.duration_minutes === "" ? undefined : Number(form.duration_minutes),
        notes: form.notes,
        schedule_id: picked?.id,
        client_token: token,
      });
      toast.success("Conversa registrada.");
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      testId="conversation-modal"
      title="Registrar conversa"
      hint={patient?.full_name || "Selecione o paciente para continuar."}
      onClose={onClose}
    >
      {step === "choose" ? (
        <div className="space-y-3.5" data-testid="conversation-step-choose">
          {!initialPatient ? (
            <>
              <CounselorField
                id="conversation-choose-counselor"
                testId="conversation-counselor"
                options={options}
                value={counselorId}
                onChange={(value) => {
                  setCounselorId(value);
                  setPatient(null);
                }}
              />
              <PatientField
                id="conversation-choose-patient"
                testId="conversation-patient"
                patients={patients}
                value={patient?.id || ""}
                onChange={(value) => setPatient(patients.find((p) => p.id === value) || null)}
              />
            </>
          ) : null}
          <p className="text-[14px] text-ink-600">Esta conversa estava agendada?</p>
          <Action icon={CalendarClock} data-testid="conversation-mode-scheduled" onClick={loadPending}>
            Conversa que estava agendada
          </Action>
          <Action
            icon={MessageSquarePlus}
            variant="secondary"
            data-testid="conversation-mode-unscheduled"
            onClick={startUnscheduled}
          >
            Conversa sem agendamento
          </Action>
        </div>
      ) : null}

      {step === "pending" ? (
        <div className="space-y-2.5" data-testid="conversation-pending-list">
          {pending === null ? (
            <p className="text-[14px] text-ink-500">Carregando agendamentos…</p>
          ) : pending.length === 0 ? (
            <>
              <p data-testid="conversation-pending-empty" className="text-[14px] text-ink-500">
                Nenhum agendamento pendente para este paciente.
              </p>
              <Action variant="secondary" data-testid="conversation-pending-fallback" onClick={startUnscheduled}>
                Registrar conversa sem agendamento
              </Action>
            </>
          ) : (
            pending.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`conversation-pending-${item.id}`}
                onClick={() => choosePending(item)}
                className="w-full rounded-xl border border-border bg-white p-3.5 text-left hover:bg-sand"
              >
                <p className="text-[14.5px] font-semibold text-ink-900">
                  {formatDay(item.scheduled_date)} às {item.scheduled_time}
                </p>
                <p className="text-[13.5px] text-ink-600">
                  {item.counselor_name || "—"} · duração prevista de {item.duration_minutes} minutos
                </p>
              </button>
            ))
          )}
          <button
            type="button"
            data-testid="conversation-pending-back"
            onClick={() => setStep("choose")}
            className="w-full rounded-xl border border-border py-2.5 text-[14px] font-semibold text-ink-700 hover:bg-sand"
          >
            Voltar
          </button>
        </div>
      ) : null}

      {step === "form" ? (
        <form onSubmit={submit} className="space-y-3.5">
          {picked ? (
            <p data-testid="conversation-selected-schedule" className="rounded-xl bg-sand p-3 text-[13.5px] text-ink-700">
              Agendamento de {formatDay(picked.scheduled_date)} às {picked.scheduled_time} · {picked.counselor_name || "—"}.
              Ajuste o horário realizado, se necessário.
            </p>
          ) : null}
          <CounselorField
            id="conversation-counselor"
            testId="conversation-counselor"
            options={options}
            value={counselorId}
            onChange={setCounselorId}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data" htmlFor="conversation-date">
              <input
                id="conversation-date"
                type="date"
                data-testid="conversation-date"
                value={form.conversation_date}
                onChange={(e) => setForm({ ...form, conversation_date: e.target.value })}
                className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
              />
            </Field>
            <Field label="Hora realizada" htmlFor="conversation-time">
              <input
                id="conversation-time"
                type="time"
                data-testid="conversation-time"
                value={form.conversation_time}
                onChange={(e) => setForm({ ...form, conversation_time: e.target.value })}
                className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo de contato" htmlFor="conversation-contact">
              <select
                id="conversation-contact"
                data-testid="conversation-contact-type"
                value={form.contact_type}
                onChange={(e) => setForm({ ...form, contact_type: e.target.value })}
                className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
              >
                {(options?.contact_types || [{ value: "presencial", label: "Presencial" }]).map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Duração (min)" hint="Opcional" htmlFor="conversation-duration">
              <input
                id="conversation-duration"
                type="number"
                min="1"
                data-testid="conversation-duration"
                value={form.duration_minutes}
                onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
              />
            </Field>
          </div>
          <Field label="Observações" htmlFor="conversation-notes">
            <textarea
              id="conversation-notes"
              rows={4}
              data-testid="conversation-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full rounded-xl border border-border bg-white p-3 text-[14.5px]"
            />
          </Field>
          <Action type="submit" icon={MessageSquarePlus} data-testid="conversation-submit" disabled={saving}>
            {saving ? "Salvando…" : "Salvar conversa"}
          </Action>
        </form>
      ) : null}
    </Modal>
  );
};

// Agendar ou reagendar conversa (duração padrão vem do backend: 45 minutos).
export const ScheduleModal = ({ patient: initialPatient, schedule, mode = "create", onClose, onSaved }) => {
  const { api } = useAuth();
  const options = useConversationOptions();
  const [token] = useState(newToken);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(initialPatient || null);
  const [counselorId, setCounselorId] = useState(schedule?.counselor_id || initialPatient?.counselor_id || "");
  const patients = useEligiblePatients(counselorId, undefined, mode === "create" && !initialPatient);
  const [form, setForm] = useState({
    scheduled_date: schedule?.scheduled_date || todayIso(),
    scheduled_time: schedule?.scheduled_time || "09:00",
    duration_minutes: schedule?.duration_minutes || "",
    reminder: schedule?.reminder || "",
  });

  useEffect(() => {
    if (options && !options.can_select_counselor && options.own_counselor_id) {
      setCounselorId(options.own_counselor_id);
    }
  }, [options]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (mode === "create" && !patient?.id) {
      toast.error("Selecione o paciente.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        scheduled_date: form.scheduled_date,
        scheduled_time: form.scheduled_time,
        duration_minutes: form.duration_minutes === "" ? undefined : Number(form.duration_minutes),
        reminder: form.reminder,
      };
      if (mode === "reschedule") {
        await api.patch(`/portal/conversations/schedules/${schedule.id}/reschedule`, body);
        toast.success("Conversa reagendada.");
      } else {
        await api.post("/portal/conversations/schedules", {
          ...body,
          patient_id: patient.id,
          counselor_id: counselorId || undefined,
          client_token: token,
        });
        toast.success("Conversa agendada.");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      testId="schedule-modal"
      title={mode === "reschedule" ? "Reagendar conversa" : "Agendar conversa"}
      hint={`${patient?.full_name || schedule?.patient_name || "Selecione o paciente"} · duração padrão de ${
        options?.default_duration_minutes || 45
      } minutos`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-3.5">
        {mode === "create" ? (
          <>
            <CounselorField
              id="schedule-counselor"
              testId="schedule-counselor"
              options={options}
              value={counselorId}
              onChange={(value) => {
                setCounselorId(value);
                if (!initialPatient) setPatient(null);
              }}
            />
            {!initialPatient ? (
              <PatientField
                id="schedule-patient"
                testId="schedule-patient"
                patients={patients}
                value={patient?.id || ""}
                onChange={(value) => setPatient(patients.find((p) => p.id === value) || null)}
              />
            ) : null}
          </>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data" htmlFor="schedule-date">
            <input
              id="schedule-date"
              type="date"
              data-testid="schedule-date"
              value={form.scheduled_date}
              onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
              className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
            />
          </Field>
          <Field label="Hora" htmlFor="schedule-time">
            <input
              id="schedule-time"
              type="time"
              data-testid="schedule-time"
              value={form.scheduled_time}
              onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })}
              className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
            />
          </Field>
        </div>
        <Field label="Duração prevista (min)" hint="Em branco = 45 minutos" htmlFor="schedule-duration">
          <input
            id="schedule-duration"
            type="number"
            min="1"
            data-testid="schedule-duration"
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
          />
        </Field>
        <Field label="Lembrete" hint="Opcional" htmlFor="schedule-reminder">
          <input
            id="schedule-reminder"
            data-testid="schedule-reminder"
            value={form.reminder}
            onChange={(e) => setForm({ ...form, reminder: e.target.value })}
            className="h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px]"
          />
        </Field>
        <Action type="submit" icon={CalendarClock} data-testid="schedule-submit" disabled={saving}>
          {saving ? "Salvando…" : mode === "reschedule" ? "Reagendar" : "Agendar"}
        </Action>
      </form>
    </Modal>
  );
};

export const ConversationItem = ({ item }) => (
  <div data-testid={`conversation-item-${item.id}`} className="rounded-xl border border-border bg-white p-3.5">
    <p className="text-[14.5px] font-semibold text-ink-900">
      {formatDay(item.conversation_date)} — {item.conversation_time}
    </p>
    <p className="mt-0.5 text-[13.5px] text-ink-600">Conselheiro: {item.counselor_name || "—"}</p>
    <p className="text-[13.5px] text-ink-600">
      Tipo: {item.contact_type_label}
      {item.duration_minutes ? ` · Duração: ${item.duration_minutes} minutos` : ""}
    </p>
    {item.notes ? <p className="mt-1.5 whitespace-pre-line text-[14px] text-ink-700">{item.notes}</p> : null}
    {item.created_by_name ? (
      <p data-testid={`conversation-registered-by-${item.id}`} className="mt-1.5 text-[13px] text-ink-500">
        Registrada no sistema por: {item.created_by_name}
        {item.created_by_label ? ` — ${item.created_by_label}` : ""}
      </p>
    ) : null}
  </div>
);

export const useConversations = (patientId, limit) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    if (!patientId) return;
    try {
      const { data: payload } = await api.get(`/portal/conversations/patients/${patientId}`, { params: { limit } });
      setData(payload);
    } catch {
      setData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, limit]);
  useEffect(() => {
    load();
  }, [load]);
  return { data, reload: load };
};

// Seção "Conversas com o Conselheiro" da ficha do paciente.
export const ConversationsPanel = ({ patient }) => {
  const { api } = useAuth();
  const { data, reload } = useConversations(patient?.id, 200);
  const [modal, setModal] = useState(null);
  if (!data) return null;
  const s = data.summary;

  return (
    <Panel testId="patient-conversations-section" className="space-y-4">
      <PanelHeader
        label="Acompanhamento"
        title="Conversas com o Conselheiro"
        hint={`Frequência esperada: a cada ${s.expected_frequency_days} dias`}
        actions={
          data.can_write ? (
            <div className="flex flex-wrap gap-2">
              <Action icon={MessageSquarePlus} data-testid="patient-register-conversation" onClick={() => setModal("conversation")}>
                Registrar conversa
              </Action>
              <Action icon={CalendarClock} variant="ghost" data-testid="patient-schedule-conversation" onClick={() => setModal("schedule")}>
                Agendar conversa
              </Action>
            </div>
          ) : null
        }
      />

      <div className="rounded-2xl bg-sand p-4">
        <div className="flex flex-wrap items-center gap-2.5">
          <StatusBadge tone={LEVEL_TONE[s.level]} testId="conversation-summary-level">
            {s.level_label}
          </StatusBadge>
          <span data-testid="conversation-summary-label" className="text-[14.5px] text-ink-700">
            {s.label}
          </span>
        </div>
        <p className="mt-2 text-[13.5px] text-ink-600">
          Última conversa: {formatDay(s.last_conversation_date)}
          {s.last_conversation_time ? ` às ${s.last_conversation_time}` : ""}
          {s.last_conversation_counselor ? ` · ${s.last_conversation_counselor}` : ""}
        </p>
        <p className="text-[13.5px] text-ink-600" data-testid="conversation-next-schedule">
          Próxima agendada:{" "}
          {data.next_schedule
            ? `${formatDay(data.next_schedule.scheduled_date)} às ${data.next_schedule.scheduled_time} (${data.next_schedule.duration_minutes} min)`
            : "nenhuma"}
        </p>
        {data.next_schedule && data.can_write ? (
          <div className="mt-3 flex flex-wrap gap-2">
            <Action
              size="sm"
              variant="ghost"
              data-testid="conversation-complete-schedule"
              onClick={() => setModal("complete")}
            >
              Registrar conversa do agendamento
            </Action>
            <Action size="sm" variant="ghost" data-testid="conversation-reschedule" onClick={() => setModal("reschedule")}>
              Reagendar
            </Action>
            <Action
              size="sm"
              variant="ghost"
              data-testid="conversation-cancel-schedule"
              onClick={async () => {
                try {
                  await api.post(`/portal/conversations/schedules/${data.next_schedule.id}/cancel`);
                  toast.success("Agendamento cancelado.");
                  reload();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail));
                }
              }}
            >
              Cancelar agendamento
            </Action>
          </div>
        ) : null}
      </div>

      <div data-testid="patient-conversation-history" className="space-y-3">
        {data.conversations.length === 0 ? (
          <p className="text-[14px] text-ink-500">Nenhuma conversa registrada.</p>
        ) : (
          data.conversations.map((item) => <ConversationItem key={item.id} item={item} />)
        )}
      </div>

      {modal === "conversation" ? (
        <ConversationModal patient={patient} onClose={() => setModal(null)} onSaved={reload} />
      ) : null}
      {modal === "complete" ? (
        <ConversationModal patient={patient} schedule={data.next_schedule} onClose={() => setModal(null)} onSaved={reload} />
      ) : null}
      {modal === "schedule" ? (
        <ScheduleModal patient={patient} onClose={() => setModal(null)} onSaved={reload} />
      ) : null}
      {modal === "reschedule" ? (
        <ScheduleModal
          patient={patient}
          schedule={data.next_schedule}
          mode="reschedule"
          onClose={() => setModal(null)}
          onSaved={reload}
        />
      ) : null}
    </Panel>
  );
};

// Bloco exibido na discussão do paciente durante a reunião clínica.
export const ConversationsMeetingCard = ({ patientId, patientName }) => {
  const { data } = useConversations(patientId, 3);
  if (!data) return null;
  const s = data.summary;
  return (
    <Panel testId="meeting-conversations-panel" className="space-y-3.5">
      <PanelHeader
        label="Conselheiro"
        title="Conversas recentes com o conselheiro"
        hint={`Última conversa: ${formatDay(s.last_conversation_date)}${
          s.days_since !== null ? ` · há ${s.days_since} dia(s)` : ""
        }${s.last_conversation_counselor ? ` · ${s.last_conversation_counselor}` : ""}`}
        actions={
          <Link
            to={`/portal/pacientes/${patientId}`}
            data-testid="meeting-conversations-see-all"
            className="text-[14px] font-semibold text-sage-700 hover:underline"
          >
            Ver todas
          </Link>
        }
      />
      {s.alert ? (
        <p
          data-testid="meeting-conversations-alert"
          className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-[14.5px] font-semibold text-red-700"
        >
          <AlertTriangle size={17} aria-hidden="true" />
          {patientName || s.patient_name} há {s.days_since} dias sem conversa registrada com conselheiro
        </p>
      ) : null}
      <div className="space-y-2.5">
        {data.conversations.length === 0 ? (
          <p className="text-[14px] text-ink-500">Nenhuma conversa registrada.</p>
        ) : (
          data.conversations.slice(0, 3).map((item) => <ConversationItem key={item.id} item={item} />)
        )}
      </div>
    </Panel>
  );
};
